// Helpers for the admin "Unallocated EVO-NetLinks" page.

export const MODE_LABELS = { 0: "Standby", 1: "Inverting", 2: "Charging", 3: "Pwr Save" };

const compactId = (value) => String(value ?? "").replace(/[\s:.-]/g, "").toUpperCase();

// Device ID with no separators, as printed by the firmware and the manufacturing tester.
export const compactDeviceId = (deviceId) => compactId(deviceId);

export const modeLabel = (mode) => {
  if (mode === null || mode === undefined || mode === "") return "-";
  return MODE_LABELS[parseInt(mode, 10)] ?? `Mode ${mode}`;
};

export const formatAge = (seconds) => {
  if (seconds === null || seconds === undefined) return "never";
  const s = Math.max(0, Math.floor(Number(seconds)));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const formatUptime = (seconds) => {
  if (seconds === null || seconds === undefined) return "-";
  const s = Math.max(0, Math.floor(Number(seconds)));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

export const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "-");

/**
 * Free-text match across Device ID (with or without separators), source IP,
 * firmware version, product type, and health status.
 */
export const matchesSearch = (device, search) => {
  const term = String(search ?? "").trim().toLowerCase();
  if (!term) return true;

  const compactTerm = compactId(term);
  if (compactTerm && compactId(device.deviceId).includes(compactTerm)) return true;

  return [device.sourceIp, device.firmwareVersion, device.productType, device.health?.status]
    .filter((value) => value !== null && value !== undefined)
    .some((value) => String(value).toLowerCase().includes(term));
};

export const SORT_VALUES = {
  status: (d) => (d.status === "online" ? 0 : 1),
  deviceId: (d) => d.deviceId,
  lastSeen: (d) => (d.lastSeen ? Date.parse(d.lastSeen) : 0),
  firstSeen: (d) => (d.firstSeen ? Date.parse(d.firstSeen) : 0),
  sourceIp: (d) => d.sourceIp ?? "",
  firmwareVersion: (d) => d.firmwareVersion ?? "",
  productType: (d) => d.productType ?? "",
  healthStatus: (d) => d.health?.status ?? "",
  uptime: (d) => d.health?.uptimeSeconds ?? -1,
  logCount: (d) => d.logCount ?? 0,
  checkinCount: (d) => d.checkinCount ?? 0,
};

export const sortDevices = (devices, sortBy, direction) => {
  const valueOf = SORT_VALUES[sortBy] ?? SORT_VALUES.lastSeen;
  const sign = direction === "asc" ? 1 : -1;
  return [...devices].sort((a, b) => {
    const va = valueOf(a);
    const vb = valueOf(b);
    if (va < vb) return -1 * sign;
    if (va > vb) return 1 * sign;
    return a.deviceId.localeCompare(b.deviceId);
  });
};

export const filterDevices = (devices, { search, status }) =>
  devices.filter(
    (device) => (status === "all" || device.status === status) && matchesSearch(device, search)
  );
